import { cn } from "cn";
import { type ComponentProps, useEffect, useRef, useState } from "react";
import { columnsOf, digitShift, startDigit } from "../lib/rolling-digits";

const DIGITS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9] as const;

/** Уменьшение движения включено: прокрутки нет, колонка сразу стоит на своей цифре. */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Колонка-барабан одной цифры. Колонка, появившаяся после первого показа
 * числа, монтируется на нуле и в следующем кадре сдвигается к своей цифре;
 * колонки первого показа стоят сразу на своих цифрах.
 */
function DigitColumn({ digit, scrollIn }: { digit: number; scrollIn: boolean }) {
  const [shown, setShown] = useState(() => startDigit(digit, scrollIn && !prefersReducedMotion()));
  useEffect(() => {
    const frame = requestAnimationFrame(() => setShown(digit));
    return () => cancelAnimationFrame(frame);
  }, [digit]);
  return (
    <span data-slot="rolling-number-digit" className="block h-lh overflow-hidden">
      <span
        className="flex flex-col transition-transform duration-300 ease-out motion-reduce:transition-none"
        style={{ transform: digitShift(shown) }}
      >
        {DIGITS.map((value) => (
          <span key={value} className="block h-lh">
            {value}
          </span>
        ))}
      </span>
    </span>
  );
}

/** Видимая часть: колонки по символам. Помнит, что число уже показывалось. */
function DigitColumns({ text }: { text: string }) {
  const shownBefore = useRef(false);
  const scrollIn = shownBefore.current;
  useEffect(() => {
    shownBefore.current = true;
  }, []);
  return (
    <span aria-hidden="true" className="inline-flex select-none">
      {columnsOf(text, scrollIn).map((column) =>
        column.digit === null ? (
          <span key={column.key} data-slot="rolling-number-symbol" className="block h-lh">
            {column.char}
          </span>
        ) : (
          <DigitColumn key={column.key} digit={column.digit} scrollIn={column.scrollIn} />
        ),
      )}
    </span>
  );
}

/**
 * Число, у которого каждая цифра — барабан 0–9, прокручивающийся к новому
 * значению. Доступный текст — число целиком (`sr-only`), барабаны скрыты от
 * дерева доступности. При `prefers-reduced-motion` значение меняется без
 * прокрутки. Цвет наследуется.
 */
function RollingNumber({
  value,
  format = String,
  className,
  ...props
}: Omit<ComponentProps<"span">, "children"> & {
  value: number;
  format?: (value: number) => string;
}) {
  const text = format(value);
  return (
    <span
      data-slot="rolling-number"
      data-value={text}
      className={cn("inline-flex tabular-nums", className)}
      {...props}
    >
      <span className="sr-only">{text}</span>
      <DigitColumns text={text} />
    </span>
  );
}

export { RollingNumber };
